import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { withReservation } from '../../../../utils/reservationSeats';
import {
  getAgendaRange,
  getCurrentStudyPlan,
  getStudyPlanMeetings
} from '../../../api/studyPlans/api';
import {
  IAgendaRange,
  IStudyPlan,
  IStudyPlanMeeting
} from '../../../api/studyPlans/types';
import type { tSharedAppState } from '../../types';
import { selectWeekCalendarStore } from './selectors';
import { IWeekCalendarState } from './types';

const initialState: IWeekCalendarState = {
  plan: { status: 'initial', data: null },
  week: null,
  catchUpTasks: null
};

// Every caller asks on mount; only a request already in flight is not repeated.
export const loadStudyPlan = createAsyncThunk<
  IStudyPlan | null,
  void,
  { state: tSharedAppState }
>('weekCalendar/loadStudyPlan', () => getCurrentStudyPlan(), {
  condition: (_arg, { getState }) =>
    selectWeekCalendarStore(getState()).plan.requestId === undefined
});

interface ILoadWeekArgs {
  from: string;
  to: string;
}

interface ILoadWeekResult {
  planId: number;
  meetings: IStudyPlanMeeting[];
  agenda: IAgendaRange | null;
}

export const loadWeek = createAsyncThunk<
  ILoadWeekResult,
  ILoadWeekArgs,
  { state: tSharedAppState }
>(
  'weekCalendar/loadWeek',
  async ({ from, to }, { getState }) => {
    const plan = selectWeekCalendarStore(getState()).plan.data;
    // The condition below guarantees a plan; both requests reject together so a half
    // week never lands as received.
    if (!plan) throw new Error('loadWeek without a study plan');
    // Every live plan joined; the week still belongs to the plan loaded first.
    const [meetingsResponse, agenda] = await Promise.all([
      getStudyPlanMeetings('current', from),
      getAgendaRange('current', from, to)
    ]);

    return {
      planId: plan.id,
      meetings: meetingsResponse.meetings,
      agenda
    };
  },
  {
    condition: ({ from }, { getState }) => {
      const { plan, week } = selectWeekCalendarStore(getState());
      if (plan.data === null) return false;
      return !(week?.from === from && week.requestId !== undefined);
    }
  }
);

const ownsWeek = (state: IWeekCalendarState, requestId: string): boolean =>
  state.week?.requestId === requestId;

const weekCalendarSlice = createSlice({
  name: 'weekCalendar',
  initialState,
  reducers: {
    // A reserve/cancel answer moves only the seat fields, so the week is patched in place.
    setMeetingReservation(
      state,
      action: PayloadAction<{ meetingId: number; reserved: boolean }>
    ) {
      const { week } = state;
      if (week?.status !== 'received') return;
      const { meetingId, reserved } = action.payload;
      week.meetings.forEach((meeting) => {
        if (meeting.id !== meetingId || meeting.reservation === reserved) return;
        Object.assign(meeting, withReservation(meeting, reserved));
      });
      // A reload in flight may have been answered before the reservation.
      delete week.requestId;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadStudyPlan.pending, (state, action) => {
        state.plan.requestId = action.meta.requestId;
        if (state.plan.status !== 'received') state.plan.status = 'loading';
      })
      .addCase(loadStudyPlan.fulfilled, (state, action) => {
        // The week belongs to the plan it was loaded for.
        if ((state.plan.data?.id ?? null) !== (action.payload?.id ?? null)) {
          state.week = null;
          state.catchUpTasks = null;
        }
        state.plan = { status: 'received', data: action.payload };
      })
      .addCase(loadStudyPlan.rejected, (state) => {
        // A failed reload keeps the answer already on screen, a plan or none.
        delete state.plan.requestId;
        if (state.plan.status === 'loading') state.plan.status = 'failed';
      })
      .addCase(loadWeek.pending, (state, action) => {
        const { from } = action.meta.arg;
        const { requestId } = action.meta;
        // Reloading the week on screen keeps it up instead of the skeleton.
        if (state.week?.from === from && state.week.status === 'received') {
          state.week.requestId = requestId;
          return;
        }
        state.week = { from, status: 'loading', meetings: [], agenda: null, requestId };
      })
      .addCase(loadWeek.fulfilled, (state, action) => {
        if (!ownsWeek(state, action.meta.requestId) || state.week === null) return;
        if (action.payload.planId !== state.plan.data?.id) return;
        const { meetings, agenda } = action.payload;
        state.week = { from: state.week.from, status: 'received', meetings, agenda };
        if (agenda?.catchUp) state.catchUpTasks = agenda.catchUp.tasksToFinish;
      })
      .addCase(loadWeek.rejected, (state, action) => {
        if (!ownsWeek(state, action.meta.requestId) || state.week === null) return;
        // A failed reload keeps the week already on screen.
        if (state.week.status === 'received') {
          delete state.week.requestId;
          return;
        }
        state.week = {
          from: state.week.from,
          status: 'failed',
          meetings: [],
          agenda: null
        };
      });
  }
});

export const { setMeetingReservation } = weekCalendarSlice.actions;
export const { reducer } = weekCalendarSlice;
